import { useEffect, useRef, useState } from "react";

type ScrollRevealProps = {
  children: React.ReactNode;
  className?: string;
  delay?: 0 | 1 | 2 | 3;
  /** Skip the hidden state when the element is already in view on mount. */
  initialVisible?: boolean;
};

export function ScrollReveal({
  children,
  className = "",
  delay = 0,
  initialVisible = false,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(initialVisible);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const markVisibleIfInView = () => {
      const rect = node.getBoundingClientRect();
      const inView =
        rect.top < window.innerHeight * 0.92 && rect.bottom > window.innerHeight * 0.08;
      if (inView) {
        setVisible(true);
        return true;
      }
      return false;
    };

    if (markVisibleIfInView()) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const delayClass = delay ? ` reveal-delay-${delay}` : "";

  return (
    <div
      ref={ref}
      className={`reveal${visible ? " visible" : ""}${delayClass} ${className}`.trim()}
    >
      {children}
    </div>
  );
}
